import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import {
  ModuleCompareActionTypes,
  useModuleCompare,
} from 'root/apps/ModuleCompareUI/context/Context';
import {
  StyledArrowLeft,
  StyledArrowRight,
  StyledTooltip,
} from 'root/apps/ModuleCompareUI/ModuleCompare.style';
import { ModuleList } from 'root/apps/ModuleCompareUI/ModuleCompare.types';

type AddModuleClickRequest = {
  accountIdToBeUpdated: string;
  moduleIdToAdd: string;
};

type AddModuleToAccountButtonProps = {
  moduleData: ModuleList[string];
  accountId: string;
  secondAccountId: string;
  moduleId: string;
  fromAccountEditable?: boolean;
  toAccountEditable?: boolean;
};

export const AddModuleToAccountButton: React.FC<AddModuleToAccountButtonProps> = ({
  moduleData,
  accountId,
  moduleId,
  secondAccountId,
  fromAccountEditable = true,
  toAccountEditable = true,
}) => {
  const { dispatch } = useModuleCompare();
  const accountID = parseInt(accountId, 10);
  const mutation = useMutation({
    mutationFn: ({ accountIdToBeUpdated }: AddModuleClickRequest) => {
      return axios.post(`/staff/account/${accountIdToBeUpdated}/module`, {
        moduleName: moduleData.name,
        description: moduleData.description,
        owner: '',
        accountID,
      });
    },
    onError: (error, { accountIdToBeUpdated, moduleIdToAdd }) => {
      // eslint-disable-next-line no-console
      console.error(
        `Error adding module:${moduleIdToAdd} to account:${accountIdToBeUpdated}`,
        error
      );
    },
  });

  const handleAddModuleClick = ({ accountIdToBeUpdated, moduleIdToAdd }: AddModuleClickRequest) => {
    const { name: moduleName } = moduleData;
    // eslint-disable-next-line no-alert
    const confirmationAgreed = window.confirm(
      `Are you sure you want to ADD module: ${
        moduleName ?? moduleIdToAdd
      } to account:${accountIdToBeUpdated}?`
    );

    if (confirmationAgreed) {
      mutation.mutate(
        { accountIdToBeUpdated, moduleIdToAdd },
        {
          onSuccess: () => {
            dispatch({
              type: ModuleCompareActionTypes.ADD_MODULE_TO_ACCOUNT,
              payload: { accountId: accountIdToBeUpdated, moduleId: moduleIdToAdd },
            });
          },
        }
      );
    }
  };

  const bothAccountsHaveModule = moduleData[accountId] && moduleData[secondAccountId];
  const neitherAccountHasModule = !moduleData[accountId] && !moduleData[secondAccountId];

  if (bothAccountsHaveModule || neitherAccountHasModule) {
    return null;
  }

  if (!moduleData[accountId] && !fromAccountEditable) {
    return (
      <StyledTooltip content={`Account ${accountId} is not editable.`}>
        <StyledArrowLeft size="1.15rem" color="gray" />
      </StyledTooltip>
    );
  }
  if (!moduleData[secondAccountId] && !toAccountEditable) {
    return (
      <StyledTooltip content={`Account ${secondAccountId} is not editable.`}>
        <StyledArrowRight size="1.15rem" color="gray" />
      </StyledTooltip>
    );
  }

  if (mutation.isLoading) {
    return <Button variant="primary" fill size="small" loading />;
  }

  const accountIdToUpdate = moduleData[accountId] ? secondAccountId : accountId;

  return (
    <StyledTooltip
      content={`Add module: ${moduleData.name ?? moduleId} to account: ${accountIdToUpdate}`}
    >
      <span
        role="button"
        tabIndex={0}
        onClick={() =>
          handleAddModuleClick({ accountIdToBeUpdated: accountIdToUpdate, moduleIdToAdd: moduleId })
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleAddModuleClick({
              accountIdToBeUpdated: accountIdToUpdate,
              moduleIdToAdd: moduleId,
            });
          }
        }}
      >
        {moduleData[accountId] ? (
          <StyledArrowRight size="1.15rem" />
        ) : (
          <StyledArrowLeft size="1.15rem" />
        )}
      </span>
    </StyledTooltip>
  );
};
